import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import StarRating from '../components/StarRating';
import { useAuth } from '../context/AuthContext';
import { 
  Search, Package, Store, FileText, SlidersHorizontal, X, 
  Grid3x3, List, ChevronDown, ChevronUp, Star, Loader2
} from 'lucide-react';

/**
 * SearchResults - Full search results page with faceted filtering
 * 
 * Features:
 * - Segmented results (All, Products, Vendors, Content)
 * - Faceted filtering sidebar
 * - Multiple sort options
 * - Grid/List view toggle
 * - Pagination
 */
const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  
  // Query parameters
  const query = searchParams.get('q') || '';
  const scope = searchParams.get('scope') || 'global';
  const segment = searchParams.get('segment') || 'all';
  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const listingType = searchParams.get('listing_type') || '';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const minRating = searchParams.get('min_rating') || '';
  const sortBy = searchParams.get('sort') || 'relevance';
  const page = parseInt(searchParams.get('page') || '1');
  
  // State
  const [results, setResults] = useState([]);
  const [facets, setFacets] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [localQuery, setLocalQuery] = useState(query);
  
  // Fetch search results
  const fetchResults = useCallback(async () => {
    if (!query) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        q: query,
        scope,
        segment,
        sort_by: sortBy,
        page: page.toString(),
        limit: '20'
      });
      
      if (category) params.append('category', category);
      if (subcategory) params.append('subcategory', subcategory);
      if (listingType) params.append('listing_type', listingType);
      if (minPrice) params.append('min_price', minPrice);
      if (maxPrice) params.append('max_price', maxPrice);
      if (minRating) params.append('min_rating', minRating);
      
      const data = await apiCall(`/api/search?${params.toString()}`);
      setResults(data.results || []);
      setFacets(data.facets || null);
      setTotal(data.total || 0);
      
      // Log search for analytics
      await apiCall('/api/search/log', {
        method: 'POST',
        body: JSON.stringify({ query, results_count: data.total })
      }).catch(() => {});
      
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, query, scope, segment, category, subcategory, listingType, minPrice, maxPrice, minRating, sortBy, page]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Update search params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    // Reset to page 1 on filter change
    if (!updates.hasOwnProperty('page')) {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  // Handle new search
  const handleSearch = (e) => {
    e.preventDefault();
    if (localQuery.trim()) {
      updateParams({ q: localQuery.trim(), page: '1' });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchParams({ q: query, scope });
  };

  // Get result type icon
  const getResultIcon = (type) => {
    switch (type) {
      case 'product':
      case 'service':
        return <Package className="h-5 w-5 text-gray-400" />;
      case 'vendor':
        return <Store className="h-5 w-5 text-gray-400" />;
      case 'content':
        return <FileText className="h-5 w-5 text-gray-400" />;
      default:
        return <Package className="h-5 w-5 text-gray-400" />;
    }
  };

  // Navigate to result
  const handleResultClick = (result) => {
    // Log click for analytics
    apiCall('/api/search/log', {
      method: 'POST',
      body: JSON.stringify({ query, results_count: total, clicked_result_id: result.id })
    }).catch(() => {});
    
    // Navigate based on type
    if (result.type === 'product' || result.type === 'service') {
      navigate(`/listings/products/${result.id}`);
    } else if (result.type === 'vendor') {
      navigate(`/vendors/${result.id}`);
    } else if (result.type === 'content') {
      navigate(`/help/${result.id}`);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = category || subcategory || listingType || minPrice || maxPrice || minRating;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4">
        {/* Search Header */}
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                value={localQuery}
                onChange={(e) => setLocalQuery(e.target.value)}
                placeholder="Search..."
                className="pl-10"
              />
            </div>
            <Button type="submit">Search</Button>
          </form>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Search Results for "{query}"
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {loading ? 'Searching...' : `${total} results found`}
              </p>
            </div>
          </div>
        </div>

        {/* Segment Tabs */}
        <Tabs value={segment} onValueChange={(v) => updateParams({ segment: v })} className="mb-4">
          <TabsList>
            <TabsTrigger value="all" className="gap-2">
              All Results
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2">
              <Package className="h-4 w-4" />
              Products & Services
            </TabsTrigger>
            <TabsTrigger value="vendors" className="gap-2">
              <Store className="h-4 w-4" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="content" className="gap-2">
              <FileText className="h-4 w-4" />
              Content & FAQ
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <div className={`w-64 flex-shrink-0 ${filtersOpen ? '' : 'hidden lg:block'}`}>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4" />
                    Filters
                  </h3>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  )}
                </div>

                {/* Category Filter */}
                {facets?.categories && facets.categories.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Category</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {facets.categories.slice(0, 10).map((cat) => (
                        <label 
                          key={cat.value}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <Checkbox
                            checked={category === cat.value}
                            onCheckedChange={(checked) => 
                              updateParams({ category: checked ? cat.value : '' })
                            }
                          />
                          <span className="text-sm text-gray-700 flex-1">{cat.value}</span>
                          <span className="text-xs text-gray-400">({cat.count})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Subcategory Filter */}
                {facets?.subcategories && facets.subcategories.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Subcategory</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {facets.subcategories.slice(0, 10).map((subcat) => (
                        <label 
                          key={subcat.value}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <Checkbox
                            checked={subcategory === subcat.value}
                            onCheckedChange={(checked) => 
                              updateParams({ subcategory: checked ? subcat.value : '' })
                            }
                          />
                          <span className="text-sm text-gray-700 flex-1">{subcat.value}</span>
                          <span className="text-xs text-gray-400">({subcat.count})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Listing Type Filter */}
                {facets?.listing_types && facets.listing_types.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-sm text-gray-700 mb-2">Type</h4>
                    <div className="space-y-2">
                      {facets.listing_types.map((type) => (
                        <label 
                          key={type.value}
                          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                        >
                          <Checkbox
                            checked={listingType === type.value}
                            onCheckedChange={(checked) => 
                              updateParams({ listing_type: checked ? type.value : '' })
                            }
                          />
                          <span className="text-sm text-gray-700 flex-1 capitalize">{type.value}</span>
                          <span className="text-xs text-gray-400">({type.count})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Range Filter */}
                <div className="mb-6">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Price Range</h4>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => updateParams({ min_price: e.target.value })}
                      className="w-24"
                    />
                    <span className="text-gray-400 self-center">-</span>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => updateParams({ max_price: e.target.value })}
                      className="w-24"
                    />
                  </div>
                </div>

                {/* Rating Filter */}
                <div className="mb-4">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Rating</h4>
                  <div className="space-y-2">
                    {[4, 3, 2].map((rating) => (
                      <label 
                        key={rating}
                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                      >
                        <Checkbox
                          checked={minRating === rating.toString()}
                          onCheckedChange={(checked) => 
                            updateParams({ min_rating: checked ? rating.toString() : '' })
                          }
                        />
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={`star-${i}`} 
                              className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                            />
                          ))}
                        </div>
                        <span className="text-sm text-gray-600">& up</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden"
                onClick={() => setFiltersOpen(!filtersOpen)}
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filters
                {filtersOpen ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />}
              </Button>
              
              <div className="flex items-center gap-4 ml-auto">
                <Select value={sortBy} onValueChange={(v) => updateParams({ sort: v })}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">Most Relevant</SelectItem>
                    <SelectItem value="price_low">Price: Low to High</SelectItem>
                    <SelectItem value="price_high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="newest">Newest First</SelectItem>
                  </SelectContent>
                </Select>
                
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid3x3 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            )}

            {/* No Results */}
            {!loading && results.length === 0 && (
              <div className="text-center py-12">
                <Search className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your search or filters to find what you're looking for.
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}

            {/* Results Grid */}
            {!loading && results.length > 0 && viewMode === 'grid' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {results.map((result) => (
                  <Card 
                    key={result.id} 
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleResultClick(result)}
                  >
                    <CardContent className="p-0">
                      {result.image ? (
                        <img 
                          src={result.image} 
                          alt={result.title}
                          className="w-full h-40 object-cover rounded-t-lg"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 rounded-t-lg flex items-center justify-center">
                          {getResultIcon(result.type)}
                        </div>
                      )}
                      <div className="p-4">
                        <Badge variant="outline" className="mb-2 capitalize text-xs">
                          {result.type}
                        </Badge>
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">
                          {result.title}
                        </h3>
                        {result.description && (
                          <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                            {result.description}
                          </p>
                        )}
                        {result.vendor_name && (
                          <p className="text-xs text-gray-500 mb-2">by {result.vendor_name}</p>
                        )}
                        <div className="flex items-center justify-between">
                          {result.price !== undefined && result.price !== null && (
                            <span className="text-lg font-bold text-gray-900">
                              ₹{result.price.toLocaleString()}
                            </span>
                          )}
                          {result.rating !== undefined && result.rating !== null && (
                            <div className="flex items-center gap-1">
                              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                              <span className="text-sm text-gray-600">
                                {result.rating.toFixed(1)}
                                {result.review_count !== undefined && (
                                  <span className="text-gray-400"> ({result.review_count})</span>
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Results List */}
            {!loading && results.length > 0 && viewMode === 'list' && (
              <div className="space-y-4">
                {results.map((result) => (
                  <Card 
                    key={result.id} 
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleResultClick(result)}
                  >
                    <CardContent className="p-4 flex gap-4">
                      {result.image ? (
                        <img 
                          src={result.image} 
                          alt={result.title}
                          className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {getResultIcon(result.type)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <Badge variant="outline" className="mb-2 capitalize text-xs">
                              {result.type}
                            </Badge>
                            <h3 className="font-semibold text-gray-900 text-lg mb-1">
                              {result.title}
                            </h3>
                            {result.description && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                {result.description}
                              </p>
                            )}
                            {result.vendor_name && (
                              <p className="text-sm text-gray-500">by {result.vendor_name}</p>
                            )}
                            {result.category && (
                              <Badge variant="secondary" className="mt-2 text-xs">
                                {result.category}
                              </Badge>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            {result.price !== undefined && result.price !== null && (
                              <span className="text-xl font-bold text-gray-900 block">
                                ₹{result.price.toLocaleString()}
                              </span>
                            )}
                            {result.rating !== undefined && result.rating !== null && (
                              <div className="flex items-center justify-end gap-1 mt-2">
                                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                                <span className="text-sm text-gray-600">
                                  {result.rating.toFixed(1)}
                                  {result.review_count !== undefined && (
                                    <span className="text-gray-400"> ({result.review_count})</span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!loading && total > 20 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => updateParams({ page: (page - 1).toString() })}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {Math.ceil(total / 20)}
                </span>
                <Button
                  variant="outline"
                  disabled={page >= Math.ceil(total / 20)}
                  onClick={() => updateParams({ page: (page + 1).toString() })}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SearchResults;
